import {
  EeClass,
  ElectionFlow,
  EstimatedCostStatistics,
  ProductInfo,
  RateStatistic,
  RequiredSubInfo,
  RequiredSubInfoPresenter,
  SavingStatus,
  StatisticsValues,
} from 'types/electionService';
import { createRequiredSubInfoPresenter } from 'global/utils/election/electionPresenters';
import {
  getEmployeeClasses,
  getProducts,
  getRequiredSubInfo,
  getStatistics,
  getSubscriberWithChoices,
  updateSubscribers,
} from 'api/operations/electionService';
import { action, computed, makeObservable, observable, runInAction, toJS } from 'mobx';
import { getRootStoreInstance } from 'store/rootStore';
import { getClassesAvailableForSelection } from 'global/services-helpers/electionService';
import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types/ProductLineId';
import { getDomainProductLine } from 'api/operations/domainServiceV2';
import { getCart } from 'api/operations/cartServiceV2';
import { sortByProductLineId } from '@insurancemenu/imenu-components-lib/dist/utils/ProductLinesHelper';
import SubscriberElectionsAdapter from './subscriberElectionsAdapter';
import EnrollmentPeriodModule from '../enrollment/enrollmentPeriodModule';

export const INITIAL_STATISTICS: StatisticsValues = {
  total: 0,
  elected: 0,
  electionsMissing: 0,
  waived: 0,
  invited: 0,
  registered: 0,
  estimatedCost: {} as EstimatedCostStatistics,
};

interface DomainData {
  groupId: string;
  enrollmentPeriodId: string;
  cartId?: string;
  /**
   * Arguments for creating an `ElectionsModule`.
   *
   * `currentFlow` (optional, default: `enrollment`):
   * The API provides both enrollment and quoting profile rules.
   * electionPresenter uses this to pick form ids and required fields for the active flow .
   */
  currentFlow?: ElectionFlow;
}

export default class ElectionsModule {
  private _rootStore = getRootStoreInstance();
  private _domainData: DomainData;
  private _enrollmentPeriodModule = new EnrollmentPeriodModule();

  private _statistics: StatisticsValues = INITIAL_STATISTICS;
  private _rateStats: RateStatistic | null = null;
  private _products: ProductInfo[] = [];
  private _classes: EeClass[] = [];
  private _requiredSubInfo: RequiredSubInfo | null = null;
  private _savingStatus: SavingStatus = 'SUCCESSFUL_SAVING';
  private _subscribers: SubscriberElectionsAdapter[] = [];
  private _domainProductLineIds: ProductLineId[] = [];

  public get enrollmentPeriodId(): string {
    return this._domainData.enrollmentPeriodId;
  }

  public get rateStats(): RateStatistic | null {
    return this._rateStats;
  }

  public get domainProductLineIds(): ProductLineId[] {
    return this._domainProductLineIds;
  }

  /**
   * The flow used to create the required sub info presenter.
   */
  public get electionFlow(): ElectionFlow {
    return this._domainData.currentFlow || 'enrollment';
  }

  /**
   * Required subscriber rules for the active flow (see {@link electionFlow}):
   * enrollment uses `editSubFormId` / `requiredFields`;
   * quoting uses `quotingSubFormId` / `quotingRequiredFields`;
   * Dependents form and `productLineRequiredInfo` follow {@link RequiredSubInfo}.
   */
  public get electionPresenter(): RequiredSubInfoPresenter | null {
    return createRequiredSubInfoPresenter(toJS(this._requiredSubInfo), this.electionFlow);
  }

  public get cartId(): string {
    return this._enrollmentPeriodModule.enrollmentProposalData?.confirmedProposal?.cartId || '';
  }

  public get requiredSubInfo(): RequiredSubInfo | null {
    return this._requiredSubInfo;
  }

  public get savingStatus(): SavingStatus {
    return this._savingStatus;
  }

  /**
   * Full list of classes for the period/group.
   */
  public get classes(): EeClass[] {
    return this._classes;
  }

  /**
   * Subset of classes allowed for selection (filtered by domain availableClasses).
   * Use for SubscriberCard so only available classes are shown in the class dropdown.
   */
  public get classesAvailableForSelection(): EeClass[] {
    return getClassesAvailableForSelection(this.classes, this._rootStore.domainStoreV2.availableClasses);
  }

  public get statistics(): StatisticsValues {
    return this._statistics;
  }

  public get products(): ProductInfo[] {
    return this._products;
  }

  public get hasSubs(): boolean {
    return this._statistics.total > 0;
  }

  public get subscribers(): SubscriberElectionsAdapter[] {
    return this._subscribers;
  }

  public get subscribersExist(): boolean {
    return this._subscribers.length > 0;
  }

  public async getStatistics(): Promise<void> {
    const res = await getStatistics(this._domainData.groupId, this._domainData.enrollmentPeriodId);
    runInAction(() => {
      const { rateStats, ...restStatistic } = res;
      this._statistics = restStatistic;
      this._rateStats = rateStats;
    });
  }

  async getProducts(): Promise<void> {
    const products = await getProducts(this._domainData.enrollmentPeriodId);
    runInAction(() => {
      this._products = products;
    });
  }

  async getClasses(): Promise<void> {
    const classes = await getEmployeeClasses(this._domainData.groupId, this._domainData.enrollmentPeriodId);
    runInAction(() => {
      this._classes = classes;
    });
  }

  async getRequiredSubInfo(): Promise<void> {
    const requiredSubInfo = await getRequiredSubInfo(this._rootStore.domainStoreV2.domainHeader.id);
    runInAction(() => {
      this._requiredSubInfo = requiredSubInfo;
    });
  }

  public async getProductLines(): Promise<void> {
    if (!this._domainData.cartId) {
      return;
    }

    const cart = await getCart(this._domainData.cartId);
    await runInAction(async () => {
      const { effectiveDate, zipCode, state, county, sic4 } = cart.lastUpdateBrowsingCriteria;

      const productLines = await getDomainProductLine({
        effectiveDate,
        zipCode,
        state,
        county,
        sic: sic4,
        agencyId: this._rootStore.userStore.imAgencyId,
        sponsorId: this._rootStore.userStore.sponsorId,
      });

      runInAction(() => {
        this._domainProductLineIds = productLines
          .filter((productLine) => productLine.rateSegment === cart.rateSegment)
          .map((productLine) => productLine.productLineId)
          .sort(sortByProductLineId);
      });
    });
  }

  public async fetchInterestedProductsCoreData(): Promise<void> {
    await Promise.all([
      this._enrollmentPeriodModule.uploadEnrollmentPeriod(this._domainData.enrollmentPeriodId),
      this.getRequiredSubInfo(),
    ]);
    await runInAction(async () => {
      await this.getProductLines();
    });
  }

  public async fetchElectionsCoreData(): Promise<void> {
    await Promise.all([
      this._enrollmentPeriodModule.uploadEnrollmentPeriod(this._domainData.enrollmentPeriodId),
      this.getRequiredSubInfo(),
      this.getClasses(),
      this.getProducts(),
      this.getStatistics(),
    ]);
  }

  public async fetchSubscriberById(subscriberId: string): Promise<void> {
    const subscriber = await getSubscriberWithChoices(subscriberId, this._domainData.enrollmentPeriodId);
    runInAction(() => {
      this._subscribers = [
        new SubscriberElectionsAdapter({
          subscriber,
          periodId: this._domainData.enrollmentPeriodId,
          electionsModule: this,
        }),
      ];
    });
  }

  public resetSubscribers(): void {
    this._subscribers = [];
  }

  // TO_DO: getSubscribers() will live here in future (after elections refactoring)

  public setSavingStatus(status: SavingStatus): void {
    this._savingStatus = status;
  }

  public async updateElectionChanges(): Promise<void> {
    this.setSavingStatus('SAVING');
    await updateSubscribers(
      this._domainData.enrollmentPeriodId,
      this._subscribers.map((subscriber) => subscriber.subscriberDelegate.subscriber),
    )
      .then(async () => {
        await this.getStatistics();
        this.setSavingStatus('SUCCESSFUL_SAVING');
      })
      .catch(() => this.setSavingStatus('FAILED_SAVING'));
  }

  constructor(params: DomainData) {
    this._domainData = {
      groupId: params.groupId,
      enrollmentPeriodId: params.enrollmentPeriodId,
      currentFlow: params.currentFlow || 'enrollment',
      cartId: params.cartId,
    };

    makeObservable<
      ElectionsModule,
      | '_domainData'
      | '_statistics'
      | '_rateStats'
      | '_products'
      | '_classes'
      | '_requiredSubInfo'
      | '_savingStatus'
      | '_subscribers'
      | '_domainProductLineIds'
    >(this, {
      _domainData: observable,
      _statistics: observable,
      _rateStats: observable,
      _products: observable,
      _classes: observable,
      _requiredSubInfo: observable,
      _savingStatus: observable,
      _subscribers: observable,
      _domainProductLineIds: observable,
      requiredSubInfo: computed,
      rateStats: computed,
      electionFlow: computed,
      electionPresenter: computed,
      savingStatus: computed,
      classes: computed,
      classesAvailableForSelection: computed,
      statistics: computed,
      products: computed,
      hasSubs: computed,
      subscribers: computed,
      subscribersExist: computed,
      domainProductLineIds: computed,
      getStatistics: action.bound,
      getProducts: action.bound,
      getClasses: action.bound,
      getRequiredSubInfo: action.bound,
      fetchElectionsCoreData: action.bound,
      fetchSubscriberById: action.bound,
      resetSubscribers: action.bound,
      setSavingStatus: action.bound,
      updateElectionChanges: action.bound,
    });
  }
}
